/**
 * Rewind Command
 * Restore code to a previous checkpoint
 */

import kleur from 'kleur';
import { getCheckpointManager, Checkpoint } from '../../checkpoints';

export async function runRewind(args: string[]): Promise<void> {
  const checkpointManager = getCheckpointManager();
  const subCommand = args[0] || 'list';

  switch (subCommand) {
    case 'list':
    case 'ls':
      listCheckpoints();
      break;

    case 'show':
      if (args[1]) {
        showCheckpoint(args[1]);
      } else {
        console.log(kleur.yellow('Usage: /rewind show <checkpoint-id>'));
      }
      break;

    case 'to':
      if (args[1]) {
        await rewindTo(args[1], args.includes('--no-git'), args.includes('--no-files'));
      } else {
        console.log(kleur.yellow('Usage: /rewind to <checkpoint-id> [--no-git] [--no-files]'));
      }
      break;

    case 'quick':
    case 'undo':
      await quickRewind();
      break;

    case 'clear':
      clearCheckpoints();
      break;

    case 'enable':
      checkpointManager.setEnabled(true);
      console.log(kleur.green('Checkpoints enabled'));
      break;

    case 'disable':
      checkpointManager.setEnabled(false);
      console.log(kleur.yellow('Checkpoints disabled'));
      break;

    case 'help':
    default:
      showHelp();
  }
}

function listCheckpoints(): void {
  const checkpointManager = getCheckpointManager();
  const checkpoints = checkpointManager.getCheckpoints();
  const summary = checkpointManager.getCheckpointsSummary();

  console.log(kleur.cyan('\n📍 Checkpoints'));
  console.log(kleur.gray('─'.repeat(60)));

  if (checkpoints.length === 0) {
    console.log(kleur.gray('No checkpoints available'));
    console.log(kleur.gray('Checkpoints are created automatically before file modifications'));
    return;
  }

  console.log(kleur.gray(`Total: ${summary.total} | This session: ${summary.sessionCount}`));
  console.log();

  // Show last 10 checkpoints
  const recent = checkpoints.slice(0, 10);

  for (const cp of recent) {
    const time = formatTimestamp(cp.timestamp);
    const branch = cp.gitState?.branch || 'unknown';
    const commit = cp.gitState?.commit?.substring(0, 7) || 'unknown';

    console.log(
      kleur.yellow(`  ${cp.id.substring(0, 12)}`) +
      kleur.gray(` (${time})`)
    );
    console.log(
      kleur.gray(`    ${cp.description}`)
    );
    console.log(
      kleur.gray(`    Branch: ${branch} @ ${commit}`) +
      (cp.triggerTool ? kleur.gray(` | Tool: ${cp.triggerTool}`) : '')
    );
    if (cp.targetFiles && cp.targetFiles.length > 0) {
      console.log(kleur.gray(`    Files: ${cp.targetFiles.join(', ')}`));
    }
    console.log();
  }

  if (checkpoints.length > 10) {
    console.log(kleur.gray(`  ... and ${checkpoints.length - 10} more checkpoints`));
  }

  console.log(kleur.gray('─'.repeat(60)));
  console.log(kleur.cyan('Commands:'));
  console.log(kleur.gray('  /rewind quick      - Rewind to last checkpoint'));
  console.log(kleur.gray('  /rewind to <id>    - Rewind to specific checkpoint'));
  console.log(kleur.gray('  /rewind show <id>  - Show checkpoint details'));
}

function showCheckpoint(id: string): void {
  const checkpointManager = getCheckpointManager();

  // Try to find by full ID or prefix
  const checkpoints = checkpointManager.getCheckpoints();
  const checkpoint = checkpoints.find(cp =>
    cp.id === id || cp.id.startsWith(id)
  );

  if (!checkpoint) {
    console.log(kleur.red(`Checkpoint not found: ${id}`));
    return;
  }

  console.log(kleur.cyan('\n📍 Checkpoint Details'));
  console.log(kleur.gray('─'.repeat(60)));
  console.log(kleur.white(`ID: `) + kleur.yellow(checkpoint.id));
  console.log(kleur.white(`Created: `) + formatTimestamp(checkpoint.timestamp));
  console.log(kleur.white(`Description: `) + checkpoint.description);
  console.log(kleur.white(`Session: `) + checkpoint.sessionId);

  if (checkpoint.triggerTool) {
    console.log(kleur.white(`Trigger Tool: `) + checkpoint.triggerTool);
  }

  console.log();
  console.log(kleur.cyan('Git State:'));
  console.log(kleur.gray(`  Branch: ${checkpoint.gitState?.branch || 'unknown'}`));
  console.log(kleur.gray(`  Commit: ${checkpoint.gitState?.commit || 'unknown'}`));

  if (checkpoint.gitState?.stagedFiles?.length) {
    console.log(kleur.gray(`  Staged: ${checkpoint.gitState.stagedFiles.join(', ')}`));
  }

  if (checkpoint.gitState?.modifiedFiles?.length) {
    console.log(kleur.gray(`  Modified: ${checkpoint.gitState.modifiedFiles.join(', ')}`));
  }

  if (checkpoint.targetFiles && checkpoint.targetFiles.length > 0) {
    console.log();
    console.log(kleur.cyan('Target Files:'));
    for (const file of checkpoint.targetFiles) {
      console.log(kleur.gray(`  - ${file}`));
    }
  }

  if (checkpoint.fileSnapshots && checkpoint.fileSnapshots.length > 0) {
    console.log();
    console.log(kleur.cyan('File Snapshots:'));
    for (const snapshot of checkpoint.fileSnapshots) {
      const status = snapshot.exists ? kleur.green('exists') : kleur.yellow('did not exist');
      console.log(kleur.gray(`  - ${snapshot.path} (${status})`));
    }
  }

  if (checkpoint.rewoundAt) {
    console.log();
    console.log(kleur.yellow(`⚠️  This checkpoint was rewound to at ${formatTimestamp(checkpoint.rewoundAt)}`));
  }
}

async function rewindTo(id: string, noGit: boolean, noFiles: boolean): Promise<void> {
  const checkpointManager = getCheckpointManager();

  // Find checkpoint
  const checkpoints = checkpointManager.getCheckpoints();
  const checkpoint = checkpoints.find(cp =>
    cp.id === id || cp.id.startsWith(id)
  );

  if (!checkpoint) {
    console.log(kleur.red(`Checkpoint not found: ${id}`));
    return;
  }

  console.log(kleur.cyan('\n🔄 Rewinding to checkpoint...'));
  console.log(kleur.gray(`ID: ${checkpoint.id}`));
  console.log(kleur.gray(`Description: ${checkpoint.description}`));
  console.log();

  const result = await checkpointManager.rewind(checkpoint.id, {
    restoreGit: !noGit,
    restoreFiles: !noFiles,
    restoreConversation: false,
    createBackup: true,
  });

  if (result.success) {
    console.log(kleur.green('✅ Rewind successful!'));

    if (result.restoredGit) {
      console.log(kleur.gray(`  Git state restored to ${checkpoint.gitState?.commit?.substring(0, 7)}`));
    }

    if (result.restoredFiles.length > 0) {
      console.log(kleur.gray(`  Restored ${result.restoredFiles.length} file(s):`));
      for (const file of result.restoredFiles) {
        console.log(kleur.gray(`    - ${file}`));
      }
    }

    if (result.backupCheckpointId) {
      console.log();
      console.log(kleur.yellow(`💾 Backup checkpoint created: ${result.backupCheckpointId.substring(0, 12)}`));
      console.log(kleur.gray('  You can rewind to the backup if needed'));
    }
  } else {
    console.log(kleur.red(`❌ Rewind failed: ${result.error}`));
  }
}

async function quickRewind(): Promise<void> {
  const checkpointManager = getCheckpointManager();
  const latest = checkpointManager.getLatestCheckpoint();

  if (!latest) {
    console.log(kleur.yellow('No checkpoints available for quick rewind'));
    return;
  }

  console.log(kleur.cyan('\n⏪ Quick Rewind'));
  console.log(kleur.gray(`Rewinding to: ${latest.description}`));
  console.log(kleur.gray(`Created: ${formatTimestamp(latest.timestamp)}`));
  console.log();

  const result = await checkpointManager.quickRewind();

  if (result.success) {
    console.log(kleur.green('✅ Quick rewind successful!'));

    if (result.restoredFiles.length > 0) {
      console.log(kleur.gray(`  Restored ${result.restoredFiles.length} file(s)`));
    }

    if (result.backupCheckpointId) {
      console.log(kleur.yellow(`💾 Backup: ${result.backupCheckpointId.substring(0, 12)}`));
    }
  } else {
    console.log(kleur.red(`❌ Quick rewind failed: ${result.error}`));
  }
}

function clearCheckpoints(): void {
  const checkpointManager = getCheckpointManager();
  checkpointManager.clearCheckpoints();
  console.log(kleur.green('✅ All checkpoints cleared'));
}

function showHelp(): void {
  console.log(kleur.cyan('\n📍 Rewind Command'));
  console.log(kleur.gray('─'.repeat(60)));
  console.log(kleur.white('Restore code to a previous checkpoint'));
  console.log();
  console.log(kleur.cyan('Usage:'));
  console.log(kleur.gray('  /rewind                  - List checkpoints'));
  console.log(kleur.gray('  /rewind list             - List checkpoints'));
  console.log(kleur.gray('  /rewind show <id>        - Show checkpoint details'));
  console.log(kleur.gray('  /rewind to <id>          - Rewind to checkpoint'));
  console.log(kleur.gray('  /rewind quick            - Rewind to last checkpoint'));
  console.log(kleur.gray('  /rewind clear            - Clear all checkpoints'));
  console.log(kleur.gray('  /rewind enable           - Enable checkpoints'));
  console.log(kleur.gray('  /rewind disable          - Disable checkpoints'));
  console.log();
  console.log(kleur.cyan('Options:'));
  console.log(kleur.gray('  --no-git                 - Skip git state restoration'));
  console.log(kleur.gray('  --no-files               - Skip file snapshot restoration'));
  console.log();
  console.log(kleur.cyan('Quick Rewind:'));
  console.log(kleur.gray('  Press Esc twice quickly to trigger quick rewind'));
}

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return date.toLocaleDateString();
}
